import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DataTable, { TableColumn } from "react-data-table-component";
import { NavbarApplicationInfo } from "../../components/CommonComponents/Navbar/NavbarApplicationInfo";
import {
  DownloadButton,
  PaymentStatusButton,
  ViewButton,
} from "../../components/CommonComponents/EditComponent/EditComponent";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { formatDate } from "../../utils/date";
import { ViewApplicationForLevel1 } from "../LevelOneReview/LevelOneApplicationDetails";
import {
  useAccountantApproveReceipt,
  useAccountantReturnReceipt,
} from "../../hooks/accountantReceipt.hooks";
import {
  useApplicationForLevel1,
  useLevel1AllReviews,
} from "../../hooks/level-1-application-approval.hooks";
import {
  TLevel1PaymentReceipt,
  useApplicationReceiptsForLevel1,
  useLevel1DemandNoteDataForPayment,
} from "../../hooks/level-1-payment-approval.hooks";
import { useSetStuffToRenderOnNavbar } from "../../contexts/NavbarCommonSpace";
import { useApplicationLogs } from "../../hooks/applications.hooks";
import { useToggle } from "../../hooks/common";
import { RenderAllLogs } from "../LevelTwo/RenderLogs";
import { RenderAllReviews } from "../LevelOneReview/RenderAllReviews";
import { Dialog } from "../../components/CommonComponents/Dialog/Dialog";

const AccountantVerificationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const applicationId = location.state?.id as number;
  const isViewOnly = Boolean(location.state?.isViewOnly);

  const [step, setStep] = useState<1 | 2>(1);
  const [remark, setRemark] = useState("");
  const [isSaved, setIsSaved] = useState(false);
  const [paymentStatusToggle, setPaymentStatusToggle] = useState<
    "Payment Acknowledgment" | "Payment Discrepancy"
  >("Payment Acknowledgment");

  const [logsDialog, toggleLogsDialog] = useToggle();
  const [reviewsDialog, toggleReviewsDialog] = useToggle();

  const { data: application, isLoading: isLoadingApp } =
    useApplicationForLevel1({ id: applicationId });

  const { data: receipts } = useApplicationReceiptsForLevel1({
    id: applicationId,
  });

  const { data: demandNoteData, isLoading: isLoadingDN } =
    useLevel1DemandNoteDataForPayment({ applicationId });

  const allLogsQuery = useApplicationLogs({ id: Number(applicationId) });
  const allReviewsQuery = useLevel1AllReviews({ id: applicationId });

  const approveMutation = useAccountantApproveReceipt();
  const returnMutation = useAccountantReturnReceipt();

  const setStuff = useSetStuffToRenderOnNavbar();

  useEffect(() => {
    if (application) {
      setStuff(
        <NavbarApplicationInfo
          applicationId={application.applicationId}
          status={application.status}
          applicationType={application.rl_application_type}
          currentPhase={application.current_phase}
        />
      );
    }
    return () => setStuff(null);
  }, [setStuff, application, step]);

  // BEFORE (kept for reference per Rule 11): inferred "active" from
  // payment_status (excluding Returned/Payment Discrepancy), falling back
  // to the last array item if everything was flagged. This broke the
  // Level-2-reject -> Level-3-confirm case: rejecting flags the receipt
  // Payment Discrepancy without creating a new receipt, so it was
  // incorrectly treated as historical even though nothing superseded it.
  // const activeReceipts = useMemo(() => {
  //   if (!receipts || receipts.length === 0) return [];
  //   const nonReturned = receipts.filter((r) => {
  //     const ps = String((r as any)?.payment_status || (r as any)?.status || "").toLowerCase();
  //     return ps !== "returned" && ps !== "payment discrepancy";
  //   });
  //   return nonReturned.length > 0 ? nonReturned : [receipts[receipts.length - 1]];
  // }, [receipts]);

  // NOW: is_current is the sole source of truth for "active vs
  // historical" — it only changes when the applicant uploads a brand new
  // receipt (see createReceipt in upload_receipts.service.js), never as a
  // side effect of a reviewer's toggle/approve/return/reject. A receipt a
  // reviewer just flagged Discrepancy (e.g. Level 2's reject) stays
  // is_current = 1 and correctly remains active for the next level.
  const activeReceipts = useMemo(() => {
    if (!receipts || receipts.length === 0) return [];
    return receipts.filter((r: any) => Number(r?.is_current) === 1);
  }, [receipts]);

  // Receipts excluded from the active reconciliation table above (already
  // superseded by a newer upload) — kept visible here as read-only audit
  // history, per the "show returned receipts, but read-only" requirement.
  const historicalReceipts = useMemo(() => {
    if (!receipts || receipts.length === 0) return [];
    return receipts.filter((r: any) => Number(r?.is_current) !== 1);
  }, [receipts]);

  const getReceiptStatusLabel = (status?: string) => {
    switch (status) {
      case "Draft":
        return "Draft (Not Submitted)";
      case "Submitted":
        return "Submitted – Under Review";
      case "Payment Discrepancy":
      case "Returned":
        return "Returned – Action Required";
      case "Payment Acknowledgment":
        return "Payment Acknowledged";
      default:
        return status || "-";
    }
  };

  const primaryReceipt = activeReceipts.length > 0 ? activeReceipts[activeReceipts.length - 1] : null;

  const totalDemandAmount = Number(
    (demandNoteData as any)?.totalAmount || demandNoteData?.totalAmount || 0
  );

  const isAcknowledged = paymentStatusToggle === "Payment Acknowledgment";

  // ORIGINAL IMPLEMENTATION PRESERVED FOR AUDIT REFERENCE:
  /*
  const receiptsTable = useMemo(
    (): TableColumn<TLevel1PaymentReceipt>[] => [
      {
        name: "Sr. No.",
        selector: (_, i) => (i || 0) + 1,
        center: true,
        grow: 0.5,
      },
      {
        name: "Application ID",
        selector: (d) => String(d?.application_id || application?.applicationId || "-"),
        center: true,
        grow: 2,
      },
      {
        name: "Receipt No",
        selector: (d) => String(d?.receipt_no || "-"),
        center: true,
        width: "150px",
      },
      {
        name: "Demand Note Amount",
        selector: () => (totalDemandAmount ? `${totalDemandAmount.toFixed(2)} INR` : "-"),
        center: true,
        width: "180px",
      },
      {
        name: "Paid Amount",
        selector: (d) => (d?.amount_paid ? `${d.amount_paid} INR` : "-"),
        center: true,
        width: "160px",
      },
      {
        name: "Payment Date",
        center: true,
        cell: (d) => (d?.payment_date ? formatDate(d.payment_date) : "-"),
        width: "160px",
      },
      {
        name: "Receipt",
        center: true,
        cell: (d) => ( ... ),
      },
      {
        name: "Action",
        center: true,
        cell: () => ( ... ),
      },
    ],
    [application, totalDemandAmount, paymentStatusToggle, isViewOnly]
  );
  */

  // MODIFIED IMPLEMENTATION: Increased column minWidths to prevent text ellipsis truncation
  const receiptsTable = useMemo(
    (): TableColumn<TLevel1PaymentReceipt>[] => [
      {
        name: "Sr. No.",
        selector: (_, i) => (i || 0) + 1,
        center: true,
        minWidth: "70px",
      },
      {
        name: "Application ID",
        selector: (d) => String(d?.application_id || application?.applicationId || "-"),
        center: true,
        minWidth: "160px",
        grow: 1,
      },
      {
        name: "Receipt No",
        selector: (d) => String(d?.receipt_no || "-"),
        center: true,
        minWidth: "220px",
      },
      {
        name: "Demand Note Amount",
        selector: () => (totalDemandAmount ? `${totalDemandAmount.toFixed(2)} INR` : "-"),
        center: true,
        minWidth: "240px",
      },
      {
        name: "Paid Amount",
        selector: (d) => (d?.amount_paid ? `${d.amount_paid} INR` : "-"),
        center: true,
        minWidth: "200px",
      },
      {
        name: "Payment Date",
        center: true,
        cell: (d) => (d?.payment_date ? formatDate(d.payment_date) : "-"),
        minWidth: "180px",
      },
      {
        name: "Receipt",
        center: true,
        minWidth: "140px",
        cell: (d) => (
          <div className="flex space-x-4">
            <ViewButton
              onClick={() => {
                const url = String(d?.receipt_file || (d as any)?.file_url || "");
                if (url) window.open(url, "_blank");
              }}
            />
            <DownloadButton
              onClick={() => {
                const url = String(d?.receipt_file || (d as any)?.file_url || "");
                if (url) window.open(url, "_blank");
              }}
            />
          </div>
        ),
      },
      {
        name: "Action",
        center: true,
        minWidth: "240px",
        cell: () => (
          <PaymentStatusButton
            paymentStatus={paymentStatusToggle}
            disabled={isViewOnly}
            onClick={() => {
              setPaymentStatusToggle((prev) =>
                prev === "Payment Acknowledgment"
                  ? "Payment Discrepancy"
                  : "Payment Acknowledgment"
              );
            }}
            tooltipText="Toggle Payment Status"
          />
        ),
      },
    ],
    [application, totalDemandAmount, paymentStatusToggle, isViewOnly]
  );

  if (isLoadingApp || isLoadingDN) {
    return (
      <div className="p-8 flex justify-center items-center min-h-[400px]">
        <div className="text-lg font-semibold text-gray-600 flex items-center gap-2">
          <i className="fa fa-circle-o-notch fa-spin text-primary text-2xl"></i>
          Loading Application & Payment Details...
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="p-8 text-center">
        <h4 className="text-xl font-bold text-red-600 mb-4">
          Application Not Found
        </h4>
        <button
          onClick={() => navigate("/accountant-receipts")}
          className="px-6 py-2 bg-primary text-white rounded-full font-semibold"
        >
          Back to Receipts Verification
        </button>
      </div>
    );
  }

  const handleApprove = () => {
    approveMutation.mutate(
      { applicationId, comment: remark },
      {
        onSuccess: () => {
          toast.success("Receipt Approved & Application Forwarded to Level 1!");
          navigate("/accountant-receipts");
        },
        onError: (err: any) => {
          toast.error(err?.message || "Failed to approve receipt");
        },
      }
    );
  };

  const handleReturn = () => {
    if (!remark.trim()) {
      toast.warning("Please enter a reviewer remark / reason before returning.");
      return;
    }

    returnMutation.mutate(
      { applicationId, comment: remark },
      {
        onSuccess: () => {
          toast.success("Receipt Returned to Applicant successfully!");
          navigate("/accountant-receipts");
        },
        onError: (err: any) => {
          toast.error(err?.message || "Failed to return receipt");
        },
      }
    );
  };

  if (logsDialog) {
    return (
      <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-8">
          <h4 className="text-center text-grey-600 font-semibold mb-8 text-2xl">
            Audit History
          </h4>
          <div className="flex justify-start mb-4">
            <button
              type="button"
              className="text-base font-bold disabled:text-gray-500 hover:underline flex items-center gap-2"
              onClick={toggleLogsDialog}
            >
              <i className="fa fa-arrow-left"></i>
              <span>Back</span>
            </button>
          </div>
          <RenderAllLogs
            logs={allLogsQuery.data || []}
            onClose={toggleLogsDialog}
          />
        </div>
      </div>
    );
  }

  if (reviewsDialog) {
    return (
      <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-8">
          <h4 className="text-center text-grey-600 font-semibold mb-8 text-2xl">
            Previous Reviews
          </h4>
          <div className="flex justify-start mb-4">
            <button
              type="button"
              className="text-base font-bold disabled:text-gray-500 hover:underline flex items-center gap-2"
              onClick={toggleReviewsDialog}
            >
              <i className="fa fa-arrow-left"></i>
              <span>Back</span>
            </button>
          </div>
          <RenderAllReviews
            reviews={allReviewsQuery.data || []}
            onClose={toggleReviewsDialog}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
      {/* PAGE 1: Application Details & Documents */}
      {step === 1 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6 mb-8">
          <ViewApplicationForLevel1 onNext={() => setStep(2)} />
        </div>
      )}

      {/* PAGE 2: Receipt Verification & Accountant Audit Remarks */}
      {step === 2 && (
        <div className="space-y-6 mb-8">
          {/* SECTION 1: PAYMENT RECEIPT RECONCILIATION DATA TABLE */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-8">
            <h4 className="text-center text-grey-600 font-semibold mb-8 text-2xl">
              Receipt Verification & Reconciliation
            </h4>

            <DataTable
              data={activeReceipts}
              keyField="receipt_id"
              columns={receiptsTable}
              customStyles={tableCustomStyles}
              className="mb-4"
            />

            {historicalReceipts.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h5 className="text-center text-gray-500 font-semibold mb-4 text-lg">
                  Historical Returned Receipts (Read-Only)
                </h5>
                <div className="space-y-2">
                  {historicalReceipts.map((r: any) => (
                    <div
                      key={r.receipt_id}
                      className="flex flex-wrap items-center justify-between gap-3 bg-gray-100 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-500"
                    >
                      <span>
                        Receipt No: <strong>{r.receipt_no || "-"}</strong>
                      </span>
                      <span>
                        Amount: <strong>{r.amount_paid ? `${r.amount_paid} INR` : "-"}</strong>
                      </span>
                      <span>
                        Date: <strong>{r.payment_date ? formatDate(r.payment_date) : "-"}</strong>
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200 text-xs font-semibold">
                        {getReceiptStatusLabel(r.payment_status)}
                      </span>
                      <div className="flex gap-3">
                        <ViewButton
                          onClick={() => {
                            const url = String(r?.receipt_file || r?.file_url || "");
                            if (url) window.open(url, "_blank");
                          }}
                        />
                        <DownloadButton
                          onClick={() => {
                            const url = String(r?.receipt_file || r?.file_url || "");
                            if (url) window.open(url, "_blank");
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* SECTION 2: ACCOUNTANT AUDIT CARD - 100% MATCH WITH ORIGINAL REVIEW REMARKS GRID */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="grid grid-cols-7 gap-4 md:gap-8">
              <div className="col-span-7 sm:col-span-5 lg:col-span-5">
                <div className="flex flex-wrap justify-between items-center mb-2 gap-4">
                  <label className="text-sm md:text-base font-bold grow">
                    Accountant Audit Remarks / Reason for Return <span className="text-red-500">*</span>
                  </label>

                  {!!allLogsQuery.data?.length && (
                    <button
                      type="button"
                      className="text-sm md:text-base font-bold disabled:text-gray-500 text-blue-600 hover:underline"
                      onClick={toggleLogsDialog}
                    >
                      Audit History
                    </button>
                  )}

                  <button
                    type="button"
                    className="text-sm md:text-base font-bold disabled:text-gray-500 text-blue-600 hover:underline flex items-center gap-1"
                    disabled={!allReviewsQuery.data?.length}
                    onClick={toggleReviewsDialog}
                  >
                    <i className="fa fa-history"></i>
                    Previous Reviews
                  </button>

                  {application.status !== "Complete" && (
                    <button
                      type="button"
                      className="text-blue-600 underline text-sm md:text-base font-bold"
                      onClick={() =>
                        window.open("/blocked-plots-viewer", "_blank")
                      }
                    >
                      List of Blocked Plots
                    </button>
                  )}
                </div>

                <textarea
                  placeholder="Add Comment"
                  value={remark}
                  disabled={isViewOnly}
                  rows={5}
                  className="h-auto w-full p-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:text-gray-600"
                  onChange={(e) => {
                    setRemark(e.target.value);
                    setIsSaved(false);
                  }}
                />
              </div>

              <div className="flex sm:flex-col col-span-7 sm:col-span-2 lg:col-span-2 gap-3 mt-8">
                <button
                  type="button"
                  disabled={!isSaved || !isAcknowledged || isViewOnly || approveMutation.isPending || returnMutation.isPending}
                  onClick={handleApprove}
                  className="bg-primary py-2 text-sm md:text-base rounded-lg font-bold text-white px-3 basis-1/2 sm:basis-auto disabled:bg-gray-300 disabled:text-gray-500"
                >
                  {approveMutation.isPending ? "Approving" : "Approve & Next"}
                </button>

                <button
                  type="button"
                  disabled={!isSaved || isAcknowledged || isViewOnly || returnMutation.isPending || approveMutation.isPending}
                  className="bg-red-400 py-2 text-sm md:text-base rounded-lg font-bold text-white px-3 basis-1/2 sm:basis-auto disabled:bg-gray-300 disabled:text-gray-500"
                  onClick={handleReturn}
                >
                  {returnMutation.isPending ? "Returning" : "Return to Applicant"}
                </button>
              </div>

              <div className="col-span-7 flex flex-wrap md:flex-nowrap justify-center gap-4 items-center border-t pt-4 mt-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="bg-black px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
                >
                  Back
                </button>

                <button
                  type="button"
                  disabled={isViewOnly || !remark.trim()}
                  onClick={() => {
                    if (!remark.trim()) {
                      toast.warning("Please enter Accountant Audit Remarks before saving.");
                      return;
                    }
                    setIsSaved(true);
                    toast.success("Accountant verification status & remarks saved!");
                  }}
                  className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountantVerificationPage;
